import {
  Component,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import {
  HighlightcardItems,
  inhalt,
  liste,
} from '../../../shared/interfaces/highlightcard-item.interface';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HighlightcardComponent } from '../highlightcard/highlightcard.component';
import HighlightCardType from '../../../shared/enums/highlightcard.type.enums';
import { Colors } from '../../../shared/enums/shared.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { ImagesService } from '../../../services/images.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-highlightcard-list',
  standalone: true,
  imports: [CommonModule, HighlightcardComponent, NgxSkeletonLoaderModule],
  templateUrl: './highlightcard-list.component.html',
  styleUrls: ['./highlightcard-list.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class HighlightcardListComponent implements OnChanges {
  @Input() items: HighlightcardItems[] = [];
  @Input() columns: string = '2';
  @Input() caasData?: CaasComponent;

  public readonly HighlightCardType = HighlightCardType;
  public type: HighlightCardType = HighlightCardType.HIGHLIGHT;
  private images: { src: string; alt: string; title: string }[] = [];
  public isLoading = true;

  constructor(
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  setCaasContent() {
    this.setCaasImage();
    this.setCaasHighlightcardItems();
    this.setCaasColumns();
  }

  setCaasColumns() {
    safeAssign(
      () => {
        this.columns =
          this.caasData?.formData.st_columns?.value?.identifier ?? '2';
      },
      'Assigning columns',
      this.errorHandler
    );
  }

  setCaasImage() {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_items?.value) {
          this.images =
            this.caasData.formData.st_items.value.flatMap((item) => {
              return (
                item.formData.st_images?.value.map((image) => {
                  if (image) {
                    let src = '';
                    let alt = '';
                    let title = '';
                    safeAssign(
                      () => {
                        src = image.formData.st_image?.value.url ?? '';
                      },
                      'Assigning src',
                      this.errorHandler
                    );
                    safeAssign(
                      () => {
                        alt =
                          this.imagesService.getAltTextById(
                            image.formData.st_image?.value.identifier ?? ''
                          ) ?? '';
                      },
                      'Assigning alt',
                      this.errorHandler
                    );
                    safeAssign(
                      () => {
                        title = image.formData.st_image_title?.value ?? '';
                      },
                      'Assigning title',
                      this.errorHandler
                    );
                    return {
                      src: src,
                      alt: alt,
                      title: title,
                    };
                  }
                  return { src: '', alt: '', title: '' };
                }) ?? []
              );
            }) ?? [];
        }
      },
      'Assigning Images',
      this.errorHandler
    );
    return this.images;
  }

  setCaasHighlightcardItems() {
    safeAssign(
      () => {
        this.items =
          this.caasData?.formData?.st_items?.value?.map(
            (item: CaasComponent, index: number) => {
              let inhalt: inhalt[] | undefined;
              let icontype = 'user';
              let iconBgColor: Colors.GREEN | Colors.BLUE | Colors.LIGHT_BLUE =
                Colors.GREEN;
              let imageSrc: string[] = [];
              let imageAlt: string[] = [];
              let imageTitle: string[] = [];
              safeAssign(
                () => {
                  let headline: string | undefined;
                  let content: string | undefined;
                  let checklist: liste[] | undefined;
                  safeAssign(
                    () => {
                      headline = item.formData.st_header?.value;
                    },
                    'Assigning headline',
                    this.errorHandler
                  );
                  safeAssign(
                    () => {
                      content = this.removeListContent(
                        item.formData.st_bodytext?.value
                      );
                    },
                    'Assigning content',
                    this.errorHandler
                  );
                  safeAssign(
                    () => {
                      checklist = this.extractListContent(
                        item.formData.st_bodytext?.value
                      );
                    },
                    'Assigning checklist',
                    this.errorHandler
                  );
                  inhalt = [
                    {
                      headline: headline,
                      content: content,
                      checklist: checklist,
                    },
                  ];
                },
                'Assigning inhalt',
                this.errorHandler
              );
              safeAssign(
                () => {
                  icontype = item.formData.st_badge_icon?.value ?? 'user';
                },
                'Assigning iconType',
                this.errorHandler
              );
              safeAssign(
                () => {
                  iconBgColor =
                    item.formData.st_badge_color?.value.identifier ===
                    'turquoise'
                      ? Colors.LIGHT_BLUE
                      : item.formData.st_badge_color?.value.identifier ===
                          'blue'
                        ? Colors.BLUE
                        : Colors.GREEN;
                },
                'Assigning iconBgColor',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageSrc = [this.images[index]?.src];
                },
                'Assigning imageSrc',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageAlt = [this.images[index]?.alt];
                },
                'Assigning imageAlt',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageTitle = [this.images[index]?.title];
                },
                'Assigning imageTitle',
                this.errorHandler
              );
              return {
                inhalt: inhalt,
                icontype: icontype,
                iconBgColor: iconBgColor,
                cta: false,
                ctaLabel: '',
                ctaType: LinkType.DEFAULT,
                ctaSrc: '',
                image: false,
                imageSrc: imageSrc,
                imageAlt: imageAlt,
                imageTitle: imageTitle,
              };
            }
          ) ?? [];
      },
      'Assigning Highlightcard Items',
      this.errorHandler
    );
  }

  extractListContent(htmlString?: string | null) {
    if (!htmlString) return undefined;
    const ulRegex = /<ul[^>]*>(.*?)<\/ul>/gs;
    const matches = [];
    let match;
    let cleanMatch;

    while ((match = ulRegex.exec(htmlString)) !== null) {
      const cleanRegex = /<li[^>]*>(.*?)(?:<\/li>|<ul[^>]*>)/gs;
      while ((cleanMatch = cleanRegex.exec(match[1])) !== null) {
        matches.push(cleanMatch[1]);
      }
    }

    if (matches.length > 0) this.type = HighlightCardType.CHECKLIST;
    return matches
      .filter((match) => match.trim().length > 0)
      .map((match) => ({ li: `<p>${match}</p>` }));
  }

  removeListContent(htmlString?: string | null) {
    if (!htmlString) return undefined;
    // Regular expression to match content between <p><ul> and </ul></p>
    const regex = /<p>\s*<ul[^>]*>.*?<\/ul>\s*<\/p>/gs;

    // Replace matched content with an empty string
    return htmlString.replace(regex, '');
  }
}
